import axios from "axios";
import { API } from "../shared";

export const PHOTO_BATCH_LIMIT = 1024 * 1024 * 1024;
const PART_LIMIT = 4 * 1024 * 1024;
const ROOT = `${API}/import/foto-upload`;

async function retryPart(action, signal) {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try { return await action(); }
    catch (error) {
      const status = error.response?.status;
      if (signal.aborted || axios.isCancel(error) || attempt === 2 || (status && status < 500 && ![408, 409, 429].includes(status))) throw error;
      await new Promise((resolve) => setTimeout(resolve, 800 * (attempt + 1)));
      if (signal.aborted) throw new axios.CanceledError();
    }
  }
}

export async function cancelPhotoBatch(session) {
  if (!session?.id) return;
  try { await axios.delete(`${ROOT}/${session.id}`, { timeout: 10000 }); }
  catch { /* A disconnected request can still be releasing its lock; temp data expires. */ }
}

export async function uploadPhotoBatch(files, sessionRef, onProgress, signal) {
  let state;
  if (sessionRef.current?.files === files) {
    try { state = (await axios.get(`${ROOT}/${sessionRef.current.id}`, { signal, timeout: 30000 })).data; }
    catch (error) { if (![404, 410].includes(error.response?.status)) throw error; }
  }
  if (!state || !["uploaden", "verwerken", "klaar"].includes(state.status)) {
    const response = await axios.post(ROOT, { files: files.map((file) => ({ name: file.name, size: file.size })) }, { signal, timeout: 30000 });
    state = response.data;
    sessionRef.current = { id: state.id, files };
  }
  const total = files.reduce((sum, file) => sum + file.size, 0);
  if (state.status !== "klaar") {
    if (!Number.isInteger(state.chunk_size) || state.chunk_size <= 0 || state.chunk_size > PART_LIMIT) throw new Error("De uploadinstellingen zijn niet beschikbaar. Probeer opnieuw.");
    onProgress(Math.floor(state.received / total * 100));
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      let offset = state.files[index].received;
      while (offset < file.size) {
        if (signal.aborted) throw new axios.CanceledError();
        const end = Math.min(offset + state.chunk_size, file.size);
        // Memory-backed small slices preserve the Safari File workaround without
        // materializing the complete (potentially 1 GB) ZIP on the phone.
        const bytes = await file.slice(offset, end).arrayBuffer();
        if (bytes.byteLength !== end - offset) throw new Error("Bestand niet volledig leesbaar. Kies het opnieuw.");
        const completed = state.received;
        const response = await retryPart(() => axios.put(`${ROOT}/${state.id}/${index}`, bytes, {
          signal, timeout: 300000, params: { offset }, headers: { "Content-Type": "application/octet-stream" },
          onUploadProgress: (event) => onProgress(Math.min(100, Math.floor((completed + Math.min(event.loaded, bytes.byteLength)) / total * 100))),
        }), signal);
        state = response.data;
        const next = state.files[index].received;
        if (next <= offset || next > file.size) throw new Error("Uploadvoortgang klopt niet. Probeer opnieuw.");
        offset = next;
        onProgress(Math.floor(state.received / total * 100));
      }
    }
  }
  const response = await retryPart(() => axios.post(`${ROOT}/${state.id}/voltooien`, {}, { signal, timeout: 120000 }), signal);
  return response.data;
}